// Bits the Pick Queue selector and the "Which wave?" page share: the
// picker's filter set (with its localStorage readers), the selector
// poll URL, the P&S wave-size rule, the response shapes for the put wall
// and Large-wave options, and the wave-starting overlay.
//
// Both screens poll /picking/selector with the SAME query string, so the
// counts a picker saw on the queue are the counts they see on the chooser
// — hence one URL builder rather than two hand-rolled strings.
import { Loader2 } from "lucide-react";

export type Tier = "low" | "high";
export type AgeFilter = "all" | "2+" | "1-2" | "<1" | "custom";
export type DeliveryFilter = "all" | "express" | "standard";
// Which sort station a Pick & Sort wave is built for. Only sent when the
// put wall is on; an older BE never sees the field.
export type SortStation = "pick_and_sort" | "put_wall";

// i18n key for a wave's type as the floor names it: a Pick & Sort wave
// bound for the Putwall is "Putwall", not "Pick & Sort" (Alex,
// 2026-10-06 — paused waves showed both as Pick & Sort). Shared by the
// paused-waves card and the admin wave list.
export const waveTypeLabelKey = (pickType: string, sortStation?: string | null): string =>
    pickType === "pick_and_sort" && sortStation === "put_wall" ? "picking_pick_type_put_wall" : `picking_pick_type_${pickType}`;

// Everything that narrows the pick pool. The queue owns these (and their
// localStorage persistence); the chooser page receives them via
// navigation state and falls back to the same storage on a direct load.
export interface PickFilters {
    tier: Tier;
    ageFilter: AgeFilter;
    deliveryFilter: DeliveryFilter;
    channelFilter: string;
    ageFrom: string;
    ageTo: string;
    dueFilter: boolean;
}

export const AGE_FILTER_STORAGE_KEY = "picking.ageFilter";
export const AGE_FROM_STORAGE_KEY = "picking.ageFrom";
export const AGE_TO_STORAGE_KEY = "picking.ageTo";
export const DELIVERY_FILTER_STORAGE_KEY = "picking.deliveryFilter";
export const CHANNEL_FILTER_STORAGE_KEY = "picking.channelFilter";
export const DUE_FILTER_STORAGE_KEY = "picking.dueFilter";
export const TIER_STORAGE_KEY = "picking.tier";
export const WAVE_SIZE_STORAGE_KEY = "picking.psWaveSize";

export const readStoredAgeFilter = (): AgeFilter => {
    try {
        // Honour legacy bucket names from before the rename.
        const raw = window.localStorage.getItem(AGE_FILTER_STORAGE_KEY)
                 ?? window.localStorage.getItem("picking.filter");
        const v = raw === "1" ? "<1" : raw === "2" ? "1-2" : raw;
        if (v === "all" || v === "<1" || v === "1-2" || v === "2+" || v === "custom") return v;
    } catch { /* ignore */ }
    return "all";
};

export const readStoredDate = (key: string): string => {
    try {
        const v = window.localStorage.getItem(key);
        if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    } catch { /* ignore */ }
    return "";
};

export const readStoredDeliveryFilter = (): DeliveryFilter => {
    try {
        const v = window.localStorage.getItem(DELIVERY_FILTER_STORAGE_KEY);
        if (v === "all" || v === "express" || v === "standard") return v;
    } catch { /* ignore */ }
    return "all";
};

export const readStoredChannelFilter = (): string => {
    try {
        const value = window.localStorage.getItem(CHANNEL_FILTER_STORAGE_KEY)?.trim().toLowerCase();
        if (value) return value;
    } catch { /* ignore */ }
    return "all";
};

export const readStoredDueFilter = (): boolean => {
    try {
        return window.localStorage.getItem(DUE_FILTER_STORAGE_KEY) === "1";
    } catch { /* ignore */ }
    return false;
};

export const readStoredTier = (): Tier => {
    try {
        const v = window.localStorage.getItem(TIER_STORAGE_KEY);
        if (v === "high" || v === "low") return v;
    } catch { /* ignore */ }
    return "low";
};

export const readStoredWaveSize = (): "small" | "large" => {
    try { return window.localStorage.getItem(WAVE_SIZE_STORAGE_KEY) === "large" ? "large" : "small"; }
    catch { return "small"; }
};

export const persistStoredWaveSize = (next: "small" | "large") => {
    try { window.localStorage.setItem(WAVE_SIZE_STORAGE_KEY, next); } catch { /* ignore */ }
};

// Write a filter set to the shared keys in one go — the chooser page does
// this with the set the queue handed it, so the two screens agree even
// if storage was stale. The clock choice is deliberately left alone: the
// handed-over dueFilter is the EFFECTIVE value (false before the cut-off
// whatever the picker chose), so writing it back would flip a stored
// preference; it persists on toggle instead.
export const persistPickFilters = (f: Partial<PickFilters>) => {
    try {
        if (f.tier) window.localStorage.setItem(TIER_STORAGE_KEY, f.tier);
        if (f.ageFilter) window.localStorage.setItem(AGE_FILTER_STORAGE_KEY, f.ageFilter);
        if (f.deliveryFilter) window.localStorage.setItem(DELIVERY_FILTER_STORAGE_KEY, f.deliveryFilter);
        if (f.channelFilter) window.localStorage.setItem(CHANNEL_FILTER_STORAGE_KEY, f.channelFilter);
        if (typeof f.ageFrom === "string") {
            if (f.ageFrom) window.localStorage.setItem(AGE_FROM_STORAGE_KEY, f.ageFrom);
            else window.localStorage.removeItem(AGE_FROM_STORAGE_KEY);
        }
        if (typeof f.ageTo === "string") {
            if (f.ageTo) window.localStorage.setItem(AGE_TO_STORAGE_KEY, f.ageTo);
            else window.localStorage.removeItem(AGE_TO_STORAGE_KEY);
        }
    } catch { /* ignore */ }
};

// Double-bay Large P&S wave option state. The per-order Large
// thresholds split the queue into two pools: `large_ready` = enough
// threshold-eligible (small/light) orders for a Large wave;
// `small_ready` = the Small pool (over-threshold orders, or everything
// while Large isn't enforced) has work. Both can be true at once — the
// pools are disjoint, so two pickers can run one of each concurrently.
export interface PasLargeInfo {
    available: boolean;
    max_orders: number | null;
    small_cap: number;
    large_ready: boolean;
    small_ready: boolean;
}

// Put wall (Pembroke): the tote-sized order pool that a put_wall sort
// station takes. Absent (older BE) or enabled:false = feature off.
export interface PutWallInfo {
    enabled: boolean;
    // Eligible put wall orders / items under the active filters (whole pool).
    orders: number;
    items: number;
    // The NEXT put wall wave: min(pool, cap).
    next_orders: number;
    next_items: number;
    cap: number;
    // Below this many eligible orders the include box defaults ON.
    min_orders: number;
}

// The picker's chosen P&S wave size, clamped to what's actually
// startable: a size whose pool is empty can't stay selected. When both
// pools have work either size is a real choice (they're disjoint); with
// the feature off everything is Small.
export const resolveWaveSize = (pasLarge: PasLargeInfo | null, choice: "small" | "large"): "small" | "large" =>
    !pasLarge?.available
        ? "small"
        : choice === "large"
            ? (pasLarge.large_ready ? "large" : "small")
            : (pasLarge.small_ready || !pasLarge.large_ready ? "small" : "large");

export const buildSelectorUrl = (f: PickFilters, waveSize: "small" | "large", includePutWall: boolean): string =>
    `/picking/selector?ageFilter=${encodeURIComponent(f.ageFilter)}`
    + `&deliveryFilter=${encodeURIComponent(f.deliveryFilter)}`
    + (f.channelFilter !== "all" ? `&channelFilter=${encodeURIComponent(f.channelFilter)}` : "")
    + (f.dueFilter ? "&dueFilter=1" : "")
    + `&tier=${encodeURIComponent(f.tier)}`
    + `&psWaveSize=${encodeURIComponent(waveSize)}`
    + `&includePutWall=${includePutWall ? "true" : "false"}`
    + (f.ageFrom ? `&ageFrom=${encodeURIComponent(f.ageFrom)}` : "")
    + (f.ageTo   ? `&ageTo=${encodeURIComponent(f.ageTo)}`     : "");

// Full-page spinner shown while we wait for /picking/wave/start (or
// /resume) to return AND the next screen to mount. The overlay locks the
// page so the picker can't tap a second card mid-allocation.
export const WaveStartingOverlay = () => (
    <div className="picking-building-overlay" role="status" aria-live="polite" aria-busy="true">
        <Loader2 className="picking-starting-spinner" size={56} strokeWidth={2.25} aria-hidden="true" />
    </div>
);

// ─── Pick Advisor (Alex, 2026-10-02) ─────────────────────────────────
// The queue's recommendation: which pick a free picker should start
// next, decided server-side from the sort queue, the pack queue, who is
// active and the picker's filtered pools. `pick` null = nothing
// qualifies right now (rule "ask_leader" — the picker is told to ask a
// team leader) or the High tier (never advised). Absent on an older BE.
export type AdvisorRule =
    | "high_tier" | "ps_join_urgent" | "morning_wall" | "morning_ps" | "single_full" | "single_sweep"
    | "pack_dry_large" | "pack_dry_single" | "drop_done_wall" | "drop_done_ps" | "cutoff_large"
    | "sort_spare_wall" | "sort_spare_ps"
    | "sort_loaded_large" | "ps_queue" | "override_1530" | "ps_join_fallback" | "ask_leader";

export interface AdvisorPick {
    pickType: "light" | "pick_and_sort" | "large";
    sortStation: SortStation;
    includePutWall: boolean;
    waveSize: "small" | "large";
    orders: number;
    // Paired waves (Alex, 2026-10-16). segments 2 = start the wave as a
    // pair (segment 1 for this picker, segment 2 on the Join card).
    // `join` names an open segment to pick up through the resume
    // endpoint instead of starting a new wave. Absent on an older BE.
    segments?: 1 | 2;
    join?: number | null;
    segment_no?: number;
}

// Paired waves: whether the warehouse allows them (the "Pick as a pair"
// toggle), from /picking/selector. Absent (older BE) = off.
export interface PairingInfo {
    enabled: boolean;
    split_pct?: number;
    // A pair is only built above this many units (Alex, 2026-10-19: 100).
    min_units?: number;
}

export interface AdvisorInfo {
    rule: AdvisorRule | string;
    pick: AdvisorPick | null;
    reason?: Record<string, unknown>;
    projections?: {
        sort_load_min?: number | null;
        pack_runway_min?: number | null;
        sorters_now?: number;
        sorters_expected?: number;
        packers_now?: number;
        lands_at?: string | null;
        packed_by?: string | null;
        latest_start?: string | null;
        last_start?: string | null;
    };
}

// A stable key for a recommendation / a start, so the two can be
// compared and logged ("followed" or not) on the wave.start event.
export const advisorPickKey = (p: AdvisorPick | null | undefined): string => {
    if (!p) return "none";
    if (p.pickType === "pick_and_sort") return p.sortStation === "put_wall" ? "put_wall" : (p.includePutWall ? "pick_and_sort+put_wall" : "pick_and_sort");
    return p.pickType;
};

export interface AdvisorStartMeta {
    rule: string;
    recommended: string;
    followed: boolean;
    suggested_only: boolean;
    // Paired waves: what the advisor said about pairing, so the shadow
    // evaluation can score paired recommendations against outcomes.
    recommended_segments?: 2;
}

export const advisorStartMeta = (advisor: AdvisorInfo | null, chosenKey: string, suggestedOnly: boolean): AdvisorStartMeta | undefined =>
    advisor
        ? {
            rule: String(advisor.rule), recommended: advisorPickKey(advisor.pick),
            followed: advisorPickKey(advisor.pick) === chosenKey, suggested_only: suggestedOnly,
            ...(advisor.pick?.segments === 2 ? { recommended_segments: 2 as const } : {}),
        }
        : undefined;

// The picker's "Pick as a pair" choice for Sorting / Putwall waves —
// per device, like the wave size. Off by default; the advisor's paired
// suggestion starts a pair regardless of this.
const PAIR_KEY = "picking:pair";
export const readStoredPair = (): boolean => {
    try { return window.localStorage.getItem(PAIR_KEY) === "true"; } catch { return false; }
};
export const persistStoredPair = (on: boolean) => {
    try { window.localStorage.setItem(PAIR_KEY, on ? "true" : "false"); } catch { /* per-device convenience only */ }
};

// The "Suggested" toggle — ON by default for every picker (Alex): the
// queue shows only the recommended pick. Per device, like the wave size.
const SUGGESTED_ONLY_KEY = "picking:suggested-only";
export const readSuggestedOnly = (): boolean => {
    try {
        const raw = window.localStorage.getItem(SUGGESTED_ONLY_KEY);
        return raw === null ? true : raw === "true";
    } catch {
        return true;
    }
};
export const persistSuggestedOnly = (on: boolean) => {
    try { window.localStorage.setItem(SUGGESTED_ONLY_KEY, on ? "true" : "false"); } catch { /* per-device convenience only */ }
};
